import React, { useState, useEffect } from 'react';
import { 
  Pill, 
  Plus, 
  Search, 
  Filter,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Edit,
  Trash2,
  Eye,
  Share2,
  User,
  AlertCircle,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  prescribing_provider?: string;
  start_date?: string;
  end_date?: string;
  status: 'active' | 'completed' | 'discontinued';
  notes?: string;
  side_effects?: string;
  next_refill_date?: string;
  refills_remaining?: number;
  pharmacy?: string;
  created_at: string;
}

export function MobileMedications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Load medications
  useEffect(() => {
    const loadMedications = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('medications')
          .select('*')
          .eq('user_id', user.id)
          .order('start_date', { ascending: false });

        if (error) throw error;
        setMedications(data || []);

      } catch (err) {
        console.error('Error loading medications:', err);
        setError('Failed to load medications');
      } finally {
        setLoading(false);
      }
    };

    loadMedications();
  }, [user]);

  const handleViewMedication = (medicationId: string) => {
    console.log('View medication:', medicationId);
  };

  const handleEditMedication = (medicationId: string) => {
    console.log('Edit medication:', medicationId);
  };

  const handleDeleteMedication = (medicationId: string) => {
    console.log('Delete medication:', medicationId);
  };

  const handleAddMedication = () => {
    console.log('Add new medication');
  };

  const handleRefillReminder = (medicationId: string) => {
    console.log('Set refill reminder for:', medicationId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'discontinued':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getUrgencyColor = (refillDate?: string) => {
    if (!refillDate) return 'text-gray-600';
    
    const daysUntilRefill = Math.ceil((new Date(refillDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilRefill <= 0) return 'text-red-600';
    if (daysUntilRefill <= 7) return 'text-orange-600';
    return 'text-green-600';
  };

  const filteredMedications = medications.filter(medication => {
    const matchesSearch = medication.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (medication.prescribing_provider && medication.prescribing_provider.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' || medication.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const activeMedications = medications.filter(m => m.status === 'active');
  const needsRefill = medications.filter(m => m.next_refill_date && new Date(m.next_refill_date) <= new Date());

  if (loading) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 pb-safe space-y-4">
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={16} className="text-red-500" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Medications</h2>
          <p className="text-sm text-gray-600">Track your prescriptions and dosages</p>
        </div>
        <Button size="sm" onClick={handleAddMedication}>
          <Plus size={16} className="mr-2" />
          Add
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Pill size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-lg font-semibold">{activeMedications.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Need Refill</p>
              <p className="text-lg font-semibold">{needsRefill.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search medications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto">
        <Button
          variant={selectedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('all')}
          className="whitespace-nowrap"
        >
          All
        </Button>
        <Button
          variant={selectedFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('active')}
          className="whitespace-nowrap"
        >
          Active
        </Button>
        <Button
          variant={selectedFilter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('completed')}
          className="whitespace-nowrap"
        >
          Completed
        </Button>
        <Button
          variant={selectedFilter === 'discontinued' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedFilter('discontinued')}
          className="whitespace-nowrap"
        >
          Discontinued
        </Button>
      </div>

      {/* Medications List */}
      <div className="space-y-3">
        {filteredMedications.length === 0 ? (
          <Card className="p-8 text-center">
            <Pill size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Medications</h3>
            <p className="text-gray-600 mb-4">Start by adding your first medication</p>
            <Button onClick={handleAddMedication}>
              <Plus size={16} className="mr-2" />
              Add Medication
            </Button>
          </Card>
        ) : (
          filteredMedications.map((medication) => (
            <Card key={medication.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Pill size={16} className="text-purple-500" />
                    <h3 className="font-medium text-gray-900">{medication.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600">{medication.dosage} • {medication.frequency}</p>
                  {medication.prescribing_provider && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <User size={12} />
                      {medication.prescribing_provider}
                    </p>
                  )}
                  {medication.start_date && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar size={12} />
                      Started: {formatDate(medication.start_date)}
                    </p>
                  )}
                  {medication.next_refill_date && (
                    <p className={`text-xs flex items-center gap-1 ${getUrgencyColor(medication.next_refill_date)}`}>
                      <Clock size={12} />
                      Refill: {formatDate(medication.next_refill_date)}
                    </p>
                  )}
                  {medication.notes && (
                    <p className="text-xs text-gray-500 mt-1">{medication.notes}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleViewMedication(medication.id)}
                  >
                    <Eye size={14} />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleEditMedication(medication.id)}
                  >
                    <Edit size={14} />
                  </Button>
                  {medication.refill_date && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleRefillReminder(medication.id)}
                    >
                      <AlertTriangle size={14} />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Status Badge */}
              <div className="mt-3 flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(medication.status)}`}>
                  {medication.status}
                </span>
                                 {medication.side_effects && (
                   <span className="text-xs text-orange-600">
                     Side effects noted
                   </span>
                 )}
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
} 